import type { Brand } from '@/domainLogic/index.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import { getOptionKey } from './roomCommon';
import { MealPlanIncludedOptionsList } from './MealPlanIncludedOptionsList';

/** What the info modal needs from a meal plan: which plan it is and what it covers. */
export type MealPlanIncludedOptionsInfo = Pick<MealPlanType, 'boardClass' | 'includedOptions'>;

type MealPlanIncludedOptionsModalProps = {
  /** Scopes the modal id (it goes into the `?modal=` history stack), e.g. the room id. */
  id: string;
  mealPlan: MealPlanIncludedOptionsInfo;
  isOpen: boolean | undefined;
  onDismiss: () => void;
  onOpenByBrowserHistory?: () => void;
};

/**
 * Info modal opened from the icon next to the meal plan name in the room area (MPH-946): the plan
 * name as header and the list of meals/drinks it includes as body. Shared by the Evo room card
 * (mounted once through EvoModals) and the legacy room panel (mounted per panel, like its
 * cancellation policies modal).
 */
export function MealPlanIncludedOptionsModal(props: MealPlanIncludedOptionsModalProps) {
  const { id, mealPlan, isOpen, onDismiss, onOpenByBrowserHistory } = props;
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  const { staticData, commonData, localization } = usePageWrapperData();
  const isMobile = commonData.isMobileDevice;
  const title = t(getOptionKey(mealPlan.boardClass, accommodationOnly));

  return (
    <OpenXModal
      isOpen={isOpen ?? false}
      isMobile={isMobile ?? true}
      brand={staticData.domainValues?.brand as Brand}
      onClose={onDismiss}
      onCloseByBrowserHistory={onDismiss}
      onOpenByBrowserHistory={onOpenByBrowserHistory}
      fullScreen={false}
      id={`meal-plan-included-options-modal-${id}`}
      aria-label={title}
      bodyBackgroundColor="backgroundLayer02"
      scrollShadows={localization?.configValuesApp?.evolutionEnabled}
      header={{
        sticky: true,
        element: <ModalHeader handleClose={onDismiss}>{title}</ModalHeader>,
      }}
    >
      <MealPlanIncludedOptionsList includedOptions={mealPlan.includedOptions} />
    </OpenXModal>
  );
}
